import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useLocalContent } from "~/sdks/LocalContent/context";
import { useResponsiveBreakpoints } from "~/hooks/useResponsiveBreakpoints";

import type {
  CategoryKey,
  ScoreKey,
  CategoryOptions,
} from "~/sdks/LocalContent/types";
import type { TransformedScoresMenuData } from "~/sdks/LocalContent/utils/transformScoresMenuData/types";

import { categories as markerCategories } from "~/sdks/LocalContent/hooks/useMarkers/constants";

import { Attribution } from "~/components/Attribution";
import { Map } from "../Map";
import { Sidebar } from "../Sidebar";
import { CategoriesToggle } from "../CategoriesToggle";
import { CommuteCalculator } from "../CommuteCalculator";

import * as styles from "./styles";

const SCORE_VALUE_THRESHOLD = 0.1;

export function Content() {
  const { t } = useTranslation(["common"]);
  const breakpoints = useResponsiveBreakpoints();
  const {
    activeMarker,
    isScoresLoading,
    isMarkersLoading,
    scoresMenu,
    scoresData,
    activeScore,
    activeScoreData,
    activeCategory,
    activeCategoryData,
    markersData,
    centerMarker,
    hideScoresList,
    locale,
    handleSelectScore,
    handleSelectCategory,
  } = useLocalContent();

  const isMobile = useMemo(() => !breakpoints.sm, [breakpoints.sm]);

  const filteredScoresMenu = useMemo(
    () =>
      ({
        ...scoresMenu,
        categories: scoresMenu.categories
          .map(
            ([categoryKey, categoryValue]) =>
              [
                categoryKey,
                {
                  ...categoryValue,
                  scores: categoryValue.scores.filter(
                    ([scoreKey, scoreValue]) => {
                      // It pertains to the "hideScores" option in the SDK
                      // https://docs.locallogic.co/docs/sdks/widgets/local-content#sdkoptions
                      if (
                        (hideScoresList.includes(scoreKey) ||
                          scoreValue.hide) &&
                        markerCategories[scoreKey].length === 0
                      ) {
                        return false;
                      }

                      return (
                        typeof scoresData?.[scoreKey]?.value !== "undefined" &&
                        scoresData[scoreKey].value > SCORE_VALUE_THRESHOLD
                      );
                    }
                  ),
                },
              ] as [CategoryKey, CategoryOptions]
          )
          .filter(
            ([, categoryValue]) =>
              Array.isArray(categoryValue.scores) &&
              categoryValue.scores.length > 0
          ),
      }) as TransformedScoresMenuData,
    [scoresMenu, scoresData]
  );

  return (
    <Map>
      {!isMobile && filteredScoresMenu.categories.length > 0 && (
        <Sidebar.Root
          isScoresLoading={isScoresLoading}
          isMarkersLoading={isMarkersLoading}
          isDefaultOpen={scoresMenu?.defaultOpen}
          activeScore={activeScore}
          activeScoreData={activeScoreData}
          markersData={markersData}
          onSelectScore={handleSelectScore}
        >
          <Sidebar.Header title={scoresMenu?.title} />
          <Sidebar.Content>
            {filteredScoresMenu.categories.map(([categoryKey, category]) => (
              <Sidebar.Category
                key={`category-${categoryKey}`}
                categoryKey={categoryKey as CategoryKey}
              >
                {category.scores.map(([scoreKey, _score]) => (
                  <Sidebar.Score
                    key={`sidebar-score-${categoryKey}-${scoreKey}`}
                    scoreKey={scoreKey as ScoreKey}
                    backgroundColor={category.colors?.background}
                    fillColor={category.colors?.fill}
                  />
                ))}
              </Sidebar.Category>
            ))}
          </Sidebar.Content>

          <Sidebar.Slider>
            <Sidebar.ScoreDetails />
          </Sidebar.Slider>
        </Sidebar.Root>
      )}

      <div className={styles.menu}>
        {typeof centerMarker !== "undefined" && (
          <CommuteCalculator.Root>
            <CommuteCalculator.Trigger />
            <CommuteCalculator.Content />
          </CommuteCalculator.Root>
        )}

        {isMobile && filteredScoresMenu.categories.length > 0 && (
          <CategoriesToggle.Root
            isScoresLoading={isScoresLoading}
            isMarkersLoading={isMarkersLoading}
            scoresData={scoresData}
            activeCategory={activeCategory}
            activeCategoryData={activeCategoryData}
            activeScore={activeScore}
            activeScoreData={activeScoreData}
            markersData={markersData}
            onSelectCategory={handleSelectCategory}
            onSelectScore={handleSelectScore}
          >
            <CategoriesToggle.Toggle />
            <CategoriesToggle.Content>
              {filteredScoresMenu?.categories.map(([categoryKey, category]) => (
                <CategoriesToggle.Category
                  key={`categories-toggle-category-${categoryKey}`}
                  categoryKey={categoryKey as CategoryKey}
                  backgroundColor={category.colors?.background}
                  fillColor={category.colors?.fill}
                />
              ))}

              <CategoriesToggle.Slider categoryKey={activeCategory}>
                {filteredScoresMenu?.categories
                  .filter(([categoryKey]) => categoryKey === activeCategory)
                  .map(([categoryKey, category]) => (
                    <React.Fragment
                      key={`categories-toggle-slider-category-${categoryKey}`}
                    >
                      {category.scores.map(([scoreKey, score]) => (
                        <CategoriesToggle.Score
                          key={`categories-toggle-slider-score-${activeCategory}-${scoreKey}`}
                          scoreKey={scoreKey as ScoreKey}
                          backgroundColor={category.colors?.background}
                          fillColor={category.colors?.fill}
                        />
                      ))}
                    </React.Fragment>
                  ))}
              </CategoriesToggle.Slider>
            </CategoriesToggle.Content>
          </CategoriesToggle.Root>
        )}
      </div>

      <Attribution sdkName="local-content" locale={locale} />
    </Map>
  );
}
